/**
 * Client error reporting.
 *
 * Posts browser-side failures to `POST /v1/telemetry/errors`, which records them
 * in the platform log beside server exceptions. Both apps caught render errors
 * in an error boundary and discarded them in production, so a customer hit a
 * crash and we learned nothing.
 *
 * This module runs *inside* the failure path, which constrains it:
 *
 *   - It must never throw. A reporter that raises inside an error boundary
 *     replaces a rendered fallback with a blank page.
 *   - It must never recurse. Reporting is deliberately fire-and-forget and is
 *     the one place the shared axios client is bypassed, because an axios
 *     interceptor that reports failures would report its own.
 *   - It must not outlive the page. `keepalive` lets the request survive the
 *     navigation that a crash usually triggers.
 *
 * Payload fields are capped to match the server's validation, which is itself
 * sized to stay under the CloudFront WAF's 8KB body limit — a larger body is
 * rejected with a headerless 403 that surfaces as an unexplained network
 * error, so an oversized report would be a crash nobody hears about.
 */

/** Caps mirror ReportClientErrorRequest::rules() on the platform. */
const LIMITS = {
  message: 512,
  name: 128,
  stack: 4000,
  componentStack: 2000,
  url: 512,
  release: 64,
  step: 128,
  fileType: 128,
  productId: 64,
} as const;

/**
 * How the error reached us. Matches the platform's `kind` enum.
 *
 * `renderer_crash` is not caught in-process at all — it is synthesized on the
 * next page load from a `crashMarker` left by a step that never cleared it,
 * which is why it carries `step`/`context` instead of a real stack trace.
 */
export type ClientErrorKind = 'render' | 'unhandled' | 'rejection' | 'renderer_crash';

export type ClientErrorApp = 'storefront' | 'console';

export interface ClientErrorReportContext {
  fileSizeBytes?: number;
  fileType?: string;
  productId?: string | number;
}

export interface ClientErrorReport {
  error: unknown;
  kind: ClientErrorKind;
  componentStack?: string | undefined;
  /** The named step, for a `renderer_crash` report — see `crashMarker`. */
  step?: string | undefined;
  context?: ClientErrorReportContext | undefined;
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/**
 * Pull a message and stack off an unknown throw.
 *
 * Anything can be thrown in JavaScript, and a rejected promise carrying a
 * string or a DOM event is common enough that assuming `Error` here would
 * discard exactly the reports that are hardest to reproduce.
 */
function describe(error: unknown): { message: string; name?: string; stack?: string } {
  if (error instanceof Error) {
    return {
      message: error.message || error.name || 'Unknown error',
      name: error.name,
      ...(error.stack === undefined ? {} : { stack: error.stack }),
    };
  }

  if (typeof error === 'string') {
    return { message: error };
  }

  try {
    return { message: `Non-Error thrown: ${JSON.stringify(error)}` };
  } catch {
    return { message: 'Non-Error thrown: unserialisable value' };
  }
}

function buildBody(
  app: ClientErrorApp,
  { error, kind, componentStack, step, context }: ClientErrorReport,
): Record<string, string> {
  const { message, name, stack } = describe(error);
  const release = import.meta.env?.VITE_APP_VERSION;

  const body: Record<string, string> = {
    app,
    kind,
    message: truncate(message, LIMITS.message),
  };

  if (name !== undefined) body.name = truncate(name, LIMITS.name);
  if (stack !== undefined) body.stack = truncate(stack, LIMITS.stack);
  if (componentStack !== undefined) {
    body.component_stack = truncate(componentStack, LIMITS.componentStack);
  }
  if (typeof window !== 'undefined') {
    body.url = truncate(window.location.href, LIMITS.url);
  }
  if (typeof release === 'string' && release !== '') {
    body.release = truncate(release, LIMITS.release);
  }
  if (step !== undefined) body.step = truncate(step, LIMITS.step);
  if (context?.fileType !== undefined) {
    body.file_type = truncate(context.fileType, LIMITS.fileType);
  }
  if (context?.fileSizeBytes !== undefined) {
    body.file_size_bytes = String(context.fileSizeBytes);
  }
  if (context?.productId !== undefined) {
    body.product_id = truncate(String(context.productId), LIMITS.productId);
  }

  return body;
}

/**
 * Send a report. Resolves to whether it was dispatched, never rejects.
 *
 * `fetch` rather than the shared axios client: axios carries interceptors that
 * refresh tokens and surface envelope errors, and a failure inside one of those
 * while reporting a failure is a loop with no floor.
 */
export async function reportClientError(
  app: ClientErrorApp,
  report: ClientErrorReport,
): Promise<boolean> {
  try {
    const origin = import.meta.env?.VITE_API_BASE_URL;

    if (typeof origin !== 'string' || origin === '') {
      return false;
    }

    // VITE_API_BASE_URL is the API *origin* only — `api/client.ts` appends
    // `/api/v1` when it builds the axios baseURL, and this module has to do the
    // same. Concatenating the path straight onto the origin produced
    // `https://api.stage.../telemetry/errors`, which matched no route; the
    // 404 came back without CORS headers, so the browser reported it as a CORS
    // failure and every report was dropped silently.
    await fetch(`${origin.replace(/\/$/, '')}/api/v1/telemetry/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(buildBody(app, report)),
      // Survives the unload that often follows a crash.
      keepalive: true,
      // No cookies: the endpoint is anonymous and a report should never
      // carry a session it does not need.
      credentials: 'omit',
    });

    return true;
  } catch {
    // Reporting failed. There is nowhere left to report that to.
    return false;
  }
}
